import {Slider} from 'platform/components';
import {Text, VStack} from 'platform/foundation';

import {type ReactNode, useState} from 'react';

import {isNotNil} from 'ramda';

import {suffixTestId, TestIdProps} from 'shared/web';

interface ChoiceSliderProps extends TestIdProps {
  label: string;
  choices: number[];
  emphasizedChoices?: number[];
  value: number;
  step?: number;
  formatChoice: (value: number) => string;
  formatAmount?: (value: number) => string;
  renderHelper?: (value: number) => ReactNode;
  isDisabled?: boolean;
  isDiscrete?: boolean;
  onChangeEnd: (value: number) => void;
}

/** The thumb follows a drag locally; only the released value leaves the slider. */
export function ChoiceSlider(props: ChoiceSliderProps) {
  const [draggedValue, setDraggedValue] = useState<number | null>(null);
  const min = props.choices.at(0) ?? 0;
  const max = props.choices.at(-1) ?? min;
  const value = draggedValue ?? props.value;

  const ticks = props.choices.map((choice) => ({
    value: choice,
    label: props.formatChoice(choice),
    small: isNotNil(props.emphasizedChoices) && !props.emphasizedChoices.includes(choice),
  }));

  const onChange = (nextValue: number) => {
    if (props.isDiscrete && !props.choices.includes(nextValue)) {
      return;
    }
    setDraggedValue(nextValue);
  };

  const onChangeEnd = (releasedValue: number) => {
    if (props.isDiscrete && !props.choices.includes(releasedValue)) {
      return;
    }
    setDraggedValue(null);
    props.onChangeEnd(releasedValue);
  };

  return (
    <VStack spacing={2} data-testid={suffixTestId('choice', props)}>
      <span data-testid={suffixTestId('label', props)}>
        <Text size="base" color="secondary">
          {props.label}
        </Text>
      </span>
      <Slider
        min={min}
        max={max === min && !props.isDiscrete ? min + 1 : max}
        step={props.isDiscrete ? props.choices : (props.step ?? props.choices)}
        ticks={ticks}
        nonlinearTicks={props.isDiscrete ? ticks : undefined}
        value={value}
        isDisabled={props.isDisabled || props.choices.length < (props.isDiscrete ? 1 : 2)}
        displayValue="auto"
        formatValue={props.formatAmount ?? props.formatChoice}
        onChange={onChange}
        onChangeEnd={onChangeEnd}
        data-testid={props['data-testid']}
      />
      {props.renderHelper?.(value)}
    </VStack>
  );
}
